import { Button, Divider, Input, Spin, TableColumnsType } from 'antd'
import Axios from 'axios'
import moment from 'moment'
import React, { useCallback } from 'react'
import * as xlsx from 'xlsx'
import ExpandedRow from '../../component/expandedRow'
import Table from '../../component/table'

const columns = (districtFilter: any[]): TableColumnsType => {
    return [
        {
            title: 'Title',
            dataIndex: 'title',
            sorter: (a: any, b: any) => (a.title > b.title ? -1 : 1),
        },
        {
            title: 'Device ID',
            dataIndex: 'deviceId',
        },
        {
            title: 'Platform',
            dataIndex: 'platform',
            filters: [
                {
                    text: 'Lora',
                    value: 'lora',
                },
                {
                    text: 'Onenet',
                    value: 'onenet',
                },
            ],
            filterMultiple: true,
            onFilter: (value: string | number | boolean, record: any) => record.platform.indexOf(value) === 0,
        },
        {
            title: 'Battery',
            dataIndex: 'battery',
        },
        {
            title: 'Phase',
            dataIndex: 'phase',
        },
        {
            title: 'Hardware ID',
            dataIndex: 'hardwareId',
        },
        {
            title: 'District',
            dataIndex: 'district',
            filters: districtFilter,
            filterMultiple: true,
            onFilter: (value: string | number | boolean, record: any) => {
                return record.location ? record.location.district.indexOf(value) > -1 : false
            },
        },
        {
            title: 'Codec',
            dataIndex: 'codec',
            filters: [
                {
                    text: 'TMS01_LR01',
                    value: 'TMS01_LR01',
                },
                {
                    text: 'TMS01_LR02',
                    value: 'TMS01_LR02',
                },
                {
                    text: 'TMS01_LR03',
                    value: 'TMS01_LR03',
                },
                {
                    text: 'TMS01_NB02',
                    value: 'TMS01_NB02',
                },
                {
                    text: 'TMS01_NB03',
                    value: 'TMS01_NB03',
                },
                {
                    text: 'TEXT',
                    value: 'TEXT',
                },
            ],
            filterMultiple: true,
            onFilter: (value: string | number | boolean, record: any) => record.codec.indexOf(value) === 0,
        },
        {
            title: 'Installed',
            dataIndex: 'installed',
            filters: [
                {
                    text: 'Yes',
                    value: 'yes',
                },
                {
                    text: 'No',
                    value: 'no',
                },
            ],
            filterMultiple: true,
            onFilter: (value: string | number | boolean, record: any) => record.installed.indexOf(value) === 0,
        },
        {
            title: 'Monitor',
            dataIndex: 'monitor',
            filters: [
                {
                    text: 'Yes',
                    value: 'yes',
                },
                {
                    text: 'No',
                    value: 'no',
                },
            ],
            filterMultiple: true,
            onFilter: (value: string | number | boolean, record: any) => record.monitor.indexOf(value) === 0,
        },
        {
            title: 'Production',
            dataIndex: 'production',
            render: text => <>{text ? 'TRUE' : 'FALSE'}</>,
            filters: [
                {
                    text: 'TRUE',
                    value: true,
                },
                {
                    text: 'FALSE',
                    value: false,
                },
            ],
            filterMultiple: true,
            onFilter: (value: string | number | boolean, record: any) => record.production === value,
        },
        {
            title: 'Last Update Time',
            dataIndex: 'lastUpdateTime',
            sorter: (a: unknown, b: unknown) => Number((a as any).lastUpdateTime > (b as any).lastUpdateTime),
        },
        {
            title: 'Install Time',
            dataIndex: 'installTime',
            sorter: (a: unknown, b: unknown) => Number((a as any).installTime > (b as any).installTime),
        },
    ]
}

interface IProps {}

const DeviceList: React.FC<IProps> = props => {
    const [state, setState] = React.useState({
        loading: false,
        contains: '',
        data: [] as any[],
        districts: [] as any[],
    })

    const fetch = useCallback(() => {
        const districts: any = []
        const exist: any = {}
        setState(prev => ({ ...prev, loading: true }))
        Axios.get('/devices', {
            params: {
                contains: state.contains,
            },
        })
            .then(res => {
                const data = res.data.map((row: any, index: number) => {
                    row.key = index
                    if (row.latest) row.battery = row.latest.voltage
                    if (row.location) row.district = row.location.district
                    row.lastUpdateTime = moment(row.lastUpdateTime).format('YYYY-MM-DD HH:mm:ss')
                    row.installTime = moment(row.installTime).format('YYYY-MM-DD HH:mm:ss')
                    if (row.latest) row.latest.recordedAt = moment(row.latest.recordedAt).format('YYYY-MM-DD HH:mm:ss')
                    if (row.location && row.location.district && !exist[row.location.district]) {
                        districts.push({
                            text: row.location.district,
                            value: row.location.district,
                        })
                        exist[row.location.district] = true
                    }

                    return row
                })
                setState(prev => ({ ...prev, data }))
            })
            .catch(err => {
                console.error(err)
            })
            .finally(() => {
                setState(prev => ({ ...prev, loading: false }))
            })
    }, [state.contains])

    React.useEffect(() => {
        fetch()
    }, [fetch])

    const onSubmit = () => {
        fetch()
    }

    const renderExpandedRow = (record: any) => {
        return <ExpandedRow record={record} key={record.title} />
    }

    const handleOnSensorIDChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = event.target
        setState(prev => ({ ...prev, contains: value }))
    }

    return (
        <Spin spinning={state.loading}>
            <div>
                <table cellPadding={5} style={{ width: '400px' }}>
                    <tbody>
                        <tr>
                            <td align="left">Sensor ID Contains</td>
                            <td>
                                <Input
                                    style={{ maxWidth: 300 }}
                                    value={state.contains}
                                    onChange={handleOnSensorIDChange}
                                />
                            </td>
                        </tr>
                        <tr>
                            <td colSpan={2}>
                                <Button
                                    type="primary"
                                    style={{ width: '100%' }}
                                    onClick={e => {
                                        onSubmit()
                                    }}
                                >
                                    Apply
                                </Button>
                            </td>
                        </tr>
                    </tbody>
                </table>

                <div style={{ marginTop: '20px' }}>
                    <Divider />
                    <Button
                        type="primary"
                        onClick={() => {
                            const wb = xlsx.utils.book_new()
                            const data = [...state.data]
                            data.forEach((row: any) => {
                                delete row.__v
                                delete row.key

                                if (row.location) {
                                    row.latitude = row.location.latitude || ''
                                    row.longitude = row.location.longitude || ''
                                    row.district = row.location.district || ''
                                    delete row.location
                                }

                                if (row.latest) {
                                    const {
                                        _id,
                                        device,
                                        __v,
                                        recordedAt,
                                        updatedAt,
                                        treeForm,
                                        routerId,
                                        district,
                                        ...other
                                    } = row.latest
                                    Object.assign(row, other)
                                    row.lastUpdateTime = recordedAt
                                    if (treeForm) {
                                        const { ...params } = treeForm
                                        Object.assign(row, params)
                                    }

                                    delete row.latest
                                }
                            })
                            const sheet = xlsx.utils.json_to_sheet(data)
                            xlsx.utils.book_append_sheet(wb, sheet)
                            xlsx.writeFile(wb, 'devices.xlsx')
                        }}
                    >
                        Download
                    </Button>
                    <Divider />
                    <Table columns={columns(state.districts)} data={state.data} expandedRowRender={renderExpandedRow} />
                </div>
            </div>
        </Spin>
    )
}

export default DeviceList
